import { m } from '@sohanscript/i18n';
import { type LocaleMeta, localeMeta } from '@sohanscript/i18n/locale-meta';
import { localizeUrl } from '@sohanscript/i18n/runtime';
import { getUrl } from '../utils/app-env';

const name = 'Sohan R. Emon';
const handle = '@sohanemon';
const siteUrl = getUrl();
// INFO: Canonical links suggest which URL to show in search results.
const canonical = 'https://sohanscript.web.app';
// INFO: Use canonical domain so social scrapers always resolve the image,
// regardless of which origin served the request (dev/preview/preprod).
const ogImage = `${canonical}/og.png`;

export const siteConfig = {
	me: name,
	url: siteUrl,
	canonical,
	handle,
	description: m.site_description(),
	ogImage,
	email: 'sohanemon@outlook.com',
	keywords: [
		'sohan',
		'emon',
		'sohanemon',
		'sohanur rahman emon',
		'sohanur r. emon',
		'sohanscript',
		'portfolio',
		'blog',
		'TypeScript',
		'JavaScript',
		'React',
		'Next.js',
		'TailwindCSS',
		'frontend',
		'web development',
		'software engineering',
		'software development',
		'frontend development',
		'full stack development',
		'fullstack development',
		'fullstack engineering',
		'full stack engineering',
		'front end development',
		'front end engineering',
		'front-end development',
		'front-end engineering',
		'front end engineering',
		'front-end engineering',
		'react',
		'nextjs',
		'tailwindcss',
		'astro',
		'tanstack',
		'supabase',
		'ai agents',
		'voice agents',
		'chatbots',
		'rag',
		'mcp servers',
		'mvp development',
		'workflow automation',
		'web scraping',
		'programmatic seo',
		'print on demand automation',
		'amazon seller tools',
	],
};

export function seo({
	title,
	description: desc,
	image,
	url,
	type = 'website',
	meta: metaExtras,
	scripts: scriptsExtras,
}: {
	title?: string;
	description?: string;
	image?: string;
	url?: string;
	type?: 'website' | 'article';
	meta?: (
		| { title: string }
		| { name: string; content: string }
		| { property: string; content: string }
	)[];
	scripts?: { type: string; children: string }[];
}): {
	meta: (
		| { title: string }
		| { name: string; content: string }
		| { property: string; content: string }
	)[];
	links: { rel: string; href: string }[];
	scripts: { type: string; children: string }[];
} {
	const resolvedTitle = title
		? `${title} - SohanScript`
		: `${siteConfig.me} — Portfolio & Blog`;
	const metaForLocale: LocaleMeta = localeMeta();
	const resolvedDesc = (desc ?? m.site_description())?.slice(0, 160);
	const resolvedImage = image ?? siteConfig.ogImage;
	const resolvedUrl = localizeUrl(url ?? siteConfig.canonical).href;

	const meta = [
		{ title: resolvedTitle },
		{ name: 'description', content: resolvedDesc },
		{ name: 'keywords', content: siteConfig.keywords.join(', ') },
		{ name: 'author', content: siteConfig.me },
		{ name: 'robots', content: 'index, follow' },
		{ name: 'revisit-after', content: '1 days' },
		{ name: 'language', content: metaForLocale.languageName },
		// Open Graph
		{ property: 'og:title', content: resolvedTitle },
		{ property: 'og:description', content: resolvedDesc },
		{ property: 'og:image', content: resolvedImage },
		{ property: 'og:url', content: resolvedUrl },
		{ property: 'og:type', content: type },
		{
			property: 'og:site_name',
			content: `${siteConfig.me} Portfolio & Blog`,
		},
		{ property: 'og:locale', content: metaForLocale.ogLocale },
		// Twitter
		{ property: 'twitter:domain', content: new URL(resolvedUrl).hostname },
		{ property: 'twitter:url', content: resolvedUrl },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:site', content: siteConfig.handle },
		{ name: 'twitter:creator', content: siteConfig.handle },
		{ name: 'twitter:title', content: resolvedTitle },
		{ name: 'twitter:description', content: resolvedDesc },
		{ name: 'twitter:url', content: resolvedUrl },
		{ name: 'twitter:image', content: resolvedImage },
		...(metaExtras ?? []),
	];

	const scripts = [
		{
			type: 'application/ld+json',
			children: JSON.stringify({
				'@context': 'https://schema.org',
				'@type': 'WebPage',
				...(title ? { name: resolvedTitle } : {}),
				description: resolvedDesc,
				url: resolvedUrl,
				'@id': resolvedUrl,
			}),
		},
		...(scriptsExtras ?? []),
	];

	const links = [{ rel: 'canonical', href: resolvedUrl }];

	return { meta, links, scripts };
}
